// WhatsApp-style client-side image compression.
//
// Goal: a single field photo should never blow a mobile-data budget. We cap
// the long edge, then step JPEG quality down (and, if needed, scale the
// dimensions down) until the encoded blob fits a small byte budget — roughly
// matching WhatsApp's "standard quality" send (~1600px / ~150KB).
//
// Re-encoding through a canvas also normalises the format (iPhone HEIC → JPEG)
// and, crucially, BAKES IN EXIF orientation: the canvas output carries no EXIF,
// so if we didn't draw the already-rotated pixels the result would be
// permanently sideways. We decode via createImageBitmap({imageOrientation:
// 'from-image'}) where available, falling back to an <img> (which modern
// browsers auto-orient).
//
// Returns { file, preview, originalSize, compressedSize } where the sizes are
// in KB and `preview` is a self-contained data URL of the FINAL blob (GC-able,
// nothing to revoke — see note on object URLs below).

const KB = 1024;

// Tunables — frugal defaults for mobile field reports.
const LONG_EDGE = 1600; // cap longest side (px); WhatsApp "standard" ≈ 1600
const TARGET_BYTES = 150 * KB; // stop once the JPEG is at/under this
const SKIP_IF_UNDER = 150 * KB; // already small + within cap → don't recompress
const Q_START = 0.8;
const Q_FLOOR = 0.5;
const Q_STEP = 0.1;
const DIM_SCALES = [1.0, 0.85, 0.7]; // progressive downscales if quality floor overshoots
const MIN_LONG_EDGE = 640; // never shrink below this

const round2 = (n) => Math.round(n * 100) / 100;
const toKB = (bytes) => Math.round(bytes / KB);

function canvasToBlob(canvas, quality) {
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob returned null'))),
      'image/jpeg',
      round2(quality),
    ),
  );
}

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

// Decode the file with EXIF orientation already applied to the pixels.
// Primary: createImageBitmap (off-thread, bakes orientation). Fallback: <img>,
// which current browsers auto-orient via `image-orientation: from-image`.
async function decodeOriented(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      /* option unsupported or decode failed — fall through to <img> */
    }
  }
  const dataURL = await blobToDataURL(file);
  const img = new Image();
  img.src = dataURL;
  if (img.decode) {
    await img.decode();
  } else {
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = () => reject(new Error('image decode failed'));
    });
  }
  return img;
}

const sourceWidth = (s) => s.width || s.naturalWidth;
const sourceHeight = (s) => s.height || s.naturalHeight;

// Draw `source` into a fresh canvas whose longest side is at most `longEdge`
// (never upscales). `flattenWhite` paints a white backdrop first so that
// transparent (PNG) pixels don't turn black when encoded to JPEG.
function drawTo(source, longEdge, flattenWhite) {
  const w0 = sourceWidth(source);
  const h0 = sourceHeight(source);
  const scale = Math.min(1, longEdge / Math.max(w0, h0));
  const w = Math.max(1, Math.round(w0 * scale));
  const h = Math.max(1, Math.round(h0 * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  if (flattenWhite) {
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h);
  }
  ctx.drawImage(source, 0, 0, w, h);
  return canvas;
}

/**
 * Resize + JPEG-compress an image File in the browser to a small, mobile-data
 * friendly size, baking in EXIF orientation and normalising the format.
 *
 * @param {File} file
 * @returns {Promise<{ file: File, preview: string, originalSize: number, compressedSize: number }>}
 *   `originalSize` / `compressedSize` are in KB. On any decode/encode failure
 *   the original file is returned untouched (never throws for a valid image).
 */
export const compressImage = async (file) => {
  const originalSize = toKB(file.size);
  const type = file.type || '';

  // Animated GIFs: a canvas captures one frame and silently drops the
  // animation, so leave GIFs untouched.
  if (type === 'image/gif') {
    const preview = await blobToDataURL(file).catch(() => '');
    return { file, preview, originalSize, compressedSize: originalSize };
  }

  // Decode with orientation baked in. If it fails, hand back the original.
  let source;
  try {
    source = await decodeOriented(file);
  } catch {
    const preview = await blobToDataURL(file).catch(() => '');
    return { file, preview, originalSize, compressedSize: originalSize };
  }

  const w0 = sourceWidth(source);
  const h0 = sourceHeight(source);
  const cappedLongEdge = Math.min(LONG_EDGE, Math.max(w0, h0)); // no upscale
  const isJpegLike = type === 'image/jpeg' || type === 'image/jpg';

  // Already small AND within the dimension cap → recompressing would only add
  // generation loss. Leave it as-is.
  if (file.size <= SKIP_IF_UNDER && Math.max(w0, h0) <= LONG_EDGE) {
    if (source.close) source.close();
    const preview = await blobToDataURL(file).catch(() => '');
    return { file, preview, originalSize, compressedSize: originalSize };
  }

  // Quality-first, then dimensions: step quality down at each size, dropping to
  // a smaller size only if the quality floor still overshoots the budget. Keep
  // the smallest blob seen so a failure to hit the budget still returns the
  // best result rather than throwing.
  let bestBlob = null;
  outer: for (const dimScale of DIM_SCALES) {
    const longEdge = Math.round(cappedLongEdge * dimScale);
    if (bestBlob && longEdge < MIN_LONG_EDGE) break;
    const canvas = drawTo(source, longEdge, !isJpegLike);
    for (let q = Q_START; q >= Q_FLOOR - 1e-9; q -= Q_STEP) {
      let blob;
      try {
        blob = await canvasToBlob(canvas, q);
      } catch {
        continue;
      }
      if (!bestBlob || blob.size < bestBlob.size) bestBlob = blob;
      if (blob.size <= TARGET_BYTES) break outer;
    }
  }
  if (source.close) source.close();

  // Re-encode failed entirely → fall back to the original file.
  if (!bestBlob) {
    const preview = await blobToDataURL(file).catch(() => '');
    return { file, preview, originalSize, compressedSize: originalSize };
  }

  // If we somehow produced a larger file (e.g. a small PNG → JPEG), keep the
  // original — the whole point is to save data.
  if (bestBlob.size >= file.size) {
    const preview = await blobToDataURL(file).catch(() => '');
    return { file, preview, originalSize, compressedSize: originalSize };
  }

  const baseName = (file.name || 'image').replace(/\.[^.]+$/, '');
  const outFile = new File([bestBlob], `${baseName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });

  // Data URL of the FINAL blob: self-contained and GC-able, so it can't leak
  // through a missing revoke (the previews here are never revoked). Switch to
  // URL.createObjectURL only alongside revokeObjectURL on unmount + remove.
  const preview = await blobToDataURL(bestBlob);

  return {
    file: outFile,
    preview,
    originalSize,
    compressedSize: toKB(bestBlob.size),
  };
};
